import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  formatPersianMoneyInput,
  parsePersianMoneyInput,
  amountToPersianWords,
  toPersianDigitsStr
} from '../utils/persianNumber';
import sellerPlanService, {
  SellerPlan,
  SellerPlanPayload,
  PlanCommissionScheme
} from '../services/sellerPlanService';
import settingService from '../services/settingService';
import './EntityManagement.css';

/**
 * مدیریت پلن‌های اشتراک فروشندگی — مدل «تخفیف داینامیک» (2026-08-16)
 * پلن = فقط «X٪ تخفیف روی کارمزد پایه». نوع/مبلغ کمیسیون از تنظیمات سراسری
 * می‌آید و پیش‌نمایش زنده نشان می‌دهد این تخفیف روی پیکربندی فعلی چه می‌کند.
 * هرگز از نرخ پایه گران‌تر نمی‌شود؛ با تغییر نرخ پایه، پلن خودکار تطبیق می‌یابد.
 */

// ─── نرخ پایهٔ سراسری از تنظیمات (آینهٔ resolveGlobalScheme بک‌اند) ───
interface BaseScheme {
  mode: 'flat' | 'tiers' | 'fixed' | 'fixed-tiers';
  percent?: number;
  tiers?: Array<{ upTo: number | null; percent: number }>;
  fixedAmount?: number;
  fixedTiers?: Array<{ upTo: number | null; amount: number }>;
}

const parseMaybeJson = (v: any): any => {
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

const resolveBaseScheme = (settings: Array<{ key: string; value: any }>): BaseScheme => {
  const get = (k: string) => settings.find((s) => s.key === k)?.value;
  const mode = String(get('commission_mode') ?? 'auto');
  const flatPercent = Number(get('commission_percentage') ?? 5) || 5;
  const percentTiers = parseMaybeJson(get('commission_tiers'));
  const fixedTiers = parseMaybeJson(get('commission_fixed_tiers'));
  const fixedAmount = Number(get('commission_fixed_amount'));

  if (mode === 'fixed_tiers') {
    if (Array.isArray(fixedTiers) && fixedTiers.length > 0) return { mode: 'fixed-tiers', fixedTiers };
  } else if (mode === 'fixed') {
    if (Number.isFinite(fixedAmount) && fixedAmount >= 0) return { mode: 'fixed', fixedAmount };
  } else if (mode === 'percent_tiers') {
    if (Array.isArray(percentTiers) && percentTiers.length > 0) return { mode: 'tiers', tiers: percentTiers };
  } else if (mode === 'auto') {
    // auto = هر پیکربندی پرشده — آینهٔ resolveGlobalScheme بک‌اند
    if (Array.isArray(fixedTiers) && fixedTiers.length > 0) return { mode: 'fixed-tiers', fixedTiers };
    if (Number.isFinite(fixedAmount) && fixedAmount > 0) return { mode: 'fixed', fixedAmount };
    if (Array.isArray(percentTiers) && percentTiers.length > 0) return { mode: 'tiers', tiers: percentTiers };
  }
  return { mode: 'flat', percent: flatPercent };
};

// اعمال تخفیف روی نرخ پایه — آینهٔ scaleSchemeByDiscount بک‌اند
const applyDiscount = (base: BaseScheme, d: number): BaseScheme => {
  const k = 1 - Math.max(0, Math.min(100, d)) / 100;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  switch (base.mode) {
    case 'tiers':
      return { mode: 'tiers', tiers: (base.tiers || []).map((t) => ({ upTo: t.upTo, percent: round2(t.percent * k) })) };
    case 'fixed':
      return { mode: 'fixed', fixedAmount: Math.round((base.fixedAmount ?? 0) * k) };
    case 'fixed-tiers':
      return { mode: 'fixed-tiers', fixedTiers: (base.fixedTiers || []).map((t) => ({ upTo: t.upTo, amount: Math.round(t.amount * k) })) };
    default:
      return { mode: 'flat', percent: round2((base.percent ?? 5) * k) };
  }
};

const BASE_MODE_LABEL: Record<string, string> = {
  flat: 'درصدی ثابت',
  tiers: 'درصدی پلکانی',
  fixed: 'مبلغ ثابت',
  'fixed-tiers': 'مبلغ‌ثابت پلکانی'
};

const describeScheme = (s: PlanCommissionScheme): string => {
  switch (s?.mode) {
    case 'discount':
      return `${s.discountPercent}% تخفیف کارمزد (داینامیک)`;
    case 'flat':
      return `${s.percent}% ثابت (قدیمی)`;
    case 'tiers':
      return `درصد پلکانی ${s.tiers?.length || 0} پله (قدیمی)`;
    case 'fixed':
      return `${(s.fixedAmount || 0).toLocaleString('fa-IR')} تومان ثابت (قدیمی)`;
    case 'fixed-tiers':
      return `مبلغ‌ثابت پلکانی ${s.fixedTiers?.length || 0} پله (قدیمی)`;
    default:
      return '—';
  }
};

const rangeLabel = (prev: number, upTo: number | null) =>
  upTo === null
    ? `مازاد بر ${prev.toLocaleString('fa-IR')}`
    : prev === 0
      ? `تا ${upTo.toLocaleString('fa-IR')} تومان`
      : `${prev.toLocaleString('fa-IR')} تا ${upTo.toLocaleString('fa-IR')}`;

const SellerPlansManagement: React.FC = () => {
  const [plans, setPlans] = useState<SellerPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLegacy, setEditingLegacy] = useState(false);
  const [baseScheme, setBaseScheme] = useState<BaseScheme | null>(null);

  // فرم — پلن = تخفیف + مشخصات؛ نوع/مبلغ کمیسیون از تنظیمات سراسری می‌آید
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [discountPercent, setDiscountPercent] = useState('30');
  const [discountQuota, setDiscountQuota] = useState('');
  const [featuresText, setFeaturesText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState('0');

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await sellerPlanService.getPlans());
    } catch (e: any) {
      toast.error(e.message || 'خطا در دریافت پلن‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    // نرخ پایهٔ فعلی برای پیش‌نمایش زنده
    settingService
      .getAllSettings()
      .then((all) => setBaseScheme(resolveBaseScheme(all as any)))
      .catch(() => setBaseScheme({ mode: 'flat', percent: 5 }));
  }, [fetchPlans]);

  const openCreate = () => {
    setEditingId(null);
    setEditingLegacy(false);
    setName('');
    setDescription('');
    setPrice('');
    setDurationDays('30');
    setDiscountPercent('30');
    setDiscountQuota('');
    setFeaturesText('');
    setIsActive(true);
    setSortOrder('0');
    setDialogOpen(true);
  };

  const openEdit = (p: SellerPlan) => {
    setEditingId(p._id);
    setEditingLegacy(p.commissionScheme?.mode !== 'discount');
    setName(p.name);
    setDescription(p.description || '');
    setPrice(String(p.price));
    setDurationDays(String(p.durationDays));
    setDiscountPercent(
      p.commissionScheme?.mode === 'discount' ? String(p.commissionScheme.discountPercent ?? 30) : '30'
    );
    setDiscountQuota(p.discountQuota === null || p.discountQuota === undefined ? '' : String(p.discountQuota));
    setFeaturesText((p.features || []).join('\n'));
    setIsActive(p.isActive);
    setSortOrder(String(p.sortOrder ?? 0));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return void toast.error('نام پلن الزامی است');
    const priceNum = Number(price);
    if (price.trim() === '' || isNaN(priceNum) || priceNum < 0)
      return void toast.error('قیمت پلن باید عددی صفر یا بزرگ‌تر باشد');
    const durNum = Number(durationDays);
    if (isNaN(durNum) || durNum < 1 || durNum > 366)
      return void toast.error('مدت پلن باید بین 1 تا 366 روز باشد');
    const d = Number(discountPercent);
    if (discountPercent.trim() === '' || isNaN(d) || d <= 0 || d > 90)
      return void toast.error('درصد تخفیف پلن باید عددی بین 1 تا 90 باشد');
    let quota: number | null = null;
    if (discountQuota.trim() !== '') {
      const q = Number(discountQuota);
      if (isNaN(q) || q <= 0) return void toast.error('سقف حجم تخفیف باید عددی مثبت یا خالی باشد');
      quota = Math.round(q);
    }

    const payload: SellerPlanPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      price: Math.round(priceNum),
      durationDays: Math.round(durNum),
      commissionScheme: { mode: 'discount', discountPercent: d },
      discountQuota: quota,
      features: featuresText.split('\n').map((f) => f.trim()).filter(Boolean),
      isActive,
      sortOrder: Number(sortOrder) || 0
    };

    try {
      setSaving(true);
      if (editingId) {
        await sellerPlanService.updatePlan(editingId, payload);
        toast.success('پلن ویرایش شد (فقط خریدهای جدید — مشترک فعال snapshot دارد)');
      } else {
        await sellerPlanService.createPlan(payload);
        toast.success('پلن ایجاد شد');
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (e: any) {
      toast.error(e.message || 'خطا در ذخیره پلن');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (p: SellerPlan) => {
    if (!window.confirm(`پلن «${p.name}» غیرفعال شود؟ (مشترک‌های فعال تا پایان دوره محفوظ‌اند)`)) return;
    try {
      await sellerPlanService.deactivatePlan(p._id);
      toast.success('پلن غیرفعال شد');
      fetchPlans();
    } catch (e: any) {
      toast.error(e.message || 'خطا');
    }
  };

  // ─── پیش‌نمایش زنده: نرخ پایه ← نرخ بعد از تخفیف ───
  const renderDiscountPreview = () => {
    if (!baseScheme) return null;
    const d = Number(discountPercent) || 0;
    const after = applyDiscount(baseScheme, d);
    return (
      <div style={{ marginTop: 10, padding: 12, background: 'var(--color-bg-default)', borderRadius: 8, fontSize: 12.5 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          🧮 پیش‌نمایش زنده — کمیسیون فعال فعلی: <span className="tag tag-sm tag-platform">{BASE_MODE_LABEL[baseScheme.mode]}</span>
        </div>
        {baseScheme.mode === 'flat' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
            <span>کمیسیون هر برداشت</span>
            <span>
              <s style={{ color: 'var(--color-text-secondary)' }}>{toPersianDigitsStr(String(baseScheme.percent ?? 5))}٪</s>{' '}
              → <strong>{toPersianDigitsStr(String(applyDiscount(baseScheme, d).percent))}٪</strong>
            </span>
          </div>
        )}
        {baseScheme.mode === 'fixed' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
            <span>کارمزد مقطوع هر برداشت</span>
            <span>
              <s style={{ color: 'var(--color-text-secondary)' }}>{(baseScheme.fixedAmount ?? 0).toLocaleString('fa-IR')}</s>{' '}
              → <strong>{(after.fixedAmount ?? 0).toLocaleString('fa-IR')} تومان</strong>
            </span>
          </div>
        )}
        {baseScheme.mode === 'tiers' &&
          (baseScheme.tiers || []).map((t, i) => {
            const prev = i === 0 ? 0 : baseScheme.tiers![i - 1].upTo ?? 0;
            const afterTier = after.tiers?.[i];
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px dashed var(--color-border)' }}>
                <span>{rangeLabel(prev, t.upTo)}</span>
                <span>
                  <s style={{ color: 'var(--color-text-secondary)' }}>{toPersianDigitsStr(String(t.percent))}٪</s>{' '}
                  → <strong>{toPersianDigitsStr(String(afterTier?.percent ?? 0))}٪</strong>
                </span>
              </div>
            );
          })}
        {baseScheme.mode === 'fixed-tiers' &&
          (baseScheme.fixedTiers || []).map((t, i) => {
            const prev = i === 0 ? 0 : baseScheme.fixedTiers![i - 1].upTo ?? 0;
            const afterTier = after.fixedTiers?.[i];
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px dashed var(--color-border)' }}>
                <span>{rangeLabel(prev, t.upTo)}</span>
                <span>
                  <s style={{ color: 'var(--color-text-secondary)' }}>{t.amount.toLocaleString('fa-IR')}</s>{' '}
                  → <strong>{(afterTier?.amount ?? 0).toLocaleString('fa-IR')} تومان</strong>
                </span>
              </div>
            );
          })}
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--color-text-secondary)', lineHeight: 1.9 }}>
          فروشنده در صفحهٔ پلن‌ها دقیقاً همین «قبل ← بعد» را با نرخ خودش می‌بیند.
          اگر بعداً نوع/مقدار کمیسیون سراسری را عوض کنید، این پلن خودکار روی نرخ جدید اعمال می‌شود.
        </div>
      </div>
    );
  };

  return (
    <div className="entity-management">
      <div className="entity-header">
        <div className="entity-title">
          <WorkspacePremiumIcon />
          <h1>پلن‌های اشتراک فروشندگی</h1>
        </div>
        <button className="edit-btn submit" onClick={openCreate}>
          <AddIcon fontSize="small" /> پلن جدید
        </button>
      </div>

      <div
        style={{
          padding: 12,
          background: 'var(--color-info-bg)',
          borderRadius: 8,
          fontSize: 13,
          margin: '12px 0',
          lineHeight: 2
        }}
      >
        پلن = <strong>«X٪ تخفیف روی کارمزد پایه»</strong> — نوع و مقدار کمیسیون از «تنظیمات سیستم» می‌آید و پلن
        داینامیک روی هر مدلی (درصدی، پلکانی، مبلغ‌ثابت، مبلغ‌ثابت پلکانی) اعمال می‌شود؛ هرگز از نرخ پایه گران‌تر نمی‌شود.
        اولویت نرخ: <strong>توافقی اختصاصی فروشگاه &gt; پلن فعال &gt; طرح سراسری</strong>.
        «سقف حجم تخفیف» = حداکثر مجموع بخش مشمول برداشت‌ها در هر دوره با نرخ پلن (همه-یا-هیچ per-برداشت).
        ویرایش/حذف پلن روی مشترک‌های فعال بی‌اثر است (snapshot لحظهٔ خرید).
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <div className="entity-table-wrapper">
          <table className="entity-table">
            <thead>
              <tr>
                <th>نام</th>
                <th>قیمت (تومان)</th>
                <th>مدت</th>
                <th>تخفیف کارمزد</th>
                <th>سقف تخفیف/دوره</th>
                <th>مشترک فعال</th>
                <th>وضعیت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
                    هنوز پلنی تعریف نشده — با «پلن جدید» شروع کنید
                  </td>
                </tr>
              )}
              {plans.map((p) => (
                <tr key={p._id} style={!p.isActive ? { opacity: 0.55 } : undefined}>
                  <td>
                    <strong>{p.name}</strong>
                    {p.description && (
                      <div style={{ fontSize: 11.5, color: 'var(--color-text-secondary)' }}>{p.description}</div>
                    )}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{p.price.toLocaleString('fa-IR')}</td>
                  <td>{toPersianDigitsStr(String(p.durationDays))} روز</td>
                  <td>
                    <span className="tag tag-sm tag-platform">{describeScheme(p.commissionScheme)}</span>
                  </td>
                  <td>
                    {p.discountQuota ? `${p.discountQuota.toLocaleString('fa-IR')} تومان` : 'نامحدود'}
                  </td>
                  <td>{toPersianDigitsStr(String(p.activeSubscribers || 0))}</td>
                  <td>
                    <span className={`tag tag-sm ${p.isActive ? 'tag-active' : ''}`}>
                      {p.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td>
                    <button className="action-btn" title="ویرایش" onClick={() => openEdit(p)}>
                      <EditIcon fontSize="small" />
                    </button>
                    {p.isActive && (
                      <button className="action-btn" title="غیرفعال‌سازی" onClick={() => handleDeactivate(p)}>
                        <BlockIcon fontSize="small" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* دیالوگ ساخت/ویرایش */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <div className="edit-dialog-title">
          <span>{editingId ? 'ویرایش پلن' : 'پلن جدید'}</span>
          <button className="details-close" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">مشخصات پلن</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>نام پلن *</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="مثال: نقره‌ای" />
                  </div>
                  <div className="edit-field">
                    <label>مدت (روز) *</label>
                    <input
                      type="number"
                      value={durationDays}
                      onChange={(e) => setDurationDays(e.target.value)}
                      min={1}
                      max={366}
                    />
                  </div>
                  <div className="edit-field">
                    <label>قیمت (تومان) *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      dir="ltr"
                      style={{ textAlign: 'right' }}
                      value={price === '' ? '' : formatPersianMoneyInput(price)}
                      onChange={(e) => setPrice(parsePersianMoneyInput(e.target.value))}
                      placeholder="مثال: 500,000"
                    />
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                      {price !== '' && !isNaN(Number(price)) ? amountToPersianWords(Number(price)) : ''}
                    </span>
                  </div>
                  <div className="edit-field">
                    <label>سقف حجم تخفیف در دوره (تومان — خالی = نامحدود)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      dir="ltr"
                      style={{ textAlign: 'right' }}
                      value={discountQuota === '' ? '' : formatPersianMoneyInput(discountQuota)}
                      onChange={(e) => setDiscountQuota(parsePersianMoneyInput(e.target.value))}
                      placeholder="مثال: 60,000,000"
                    />
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', minHeight: 14 }}>
                      {discountQuota !== '' && !isNaN(Number(discountQuota))
                        ? amountToPersianWords(Number(discountQuota))
                        : 'جلوی «انباشت بالانس و برداشت یک‌جای ارزان» را می‌گیرد'}
                    </span>
                  </div>
                  <div className="edit-field full">
                    <label>توضیح کوتاه</label>
                    <input value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>
                  <div className="edit-field full">
                    <label>مزایا (هر خط یک مورد — نمایش در صفحهٔ فروشنده)</label>
                    <textarea
                      rows={3}
                      value={featuresText}
                      onChange={(e) => setFeaturesText(e.target.value)}
                      placeholder={'نشان فروشندهٔ حرفه‌ای\nپشتیبانی اولویت‌دار'}
                    />
                  </div>
                  <div className="edit-field">
                    <label>ترتیب نمایش</label>
                    <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
                  </div>
                  <div className="edit-field">
                    <label>وضعیت</label>
                    <select
                      value={isActive ? '1' : '0'}
                      onChange={(e) => setIsActive(e.target.value === '1')}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)' }}
                    >
                      <option value="1">فعال (قابل خرید)</option>
                      <option value="0">غیرفعال</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="edit-card">
              <div className="edit-card-header">تخفیف کارمزد (داینامیک)</div>
              <div className="edit-card-body">
                {editingLegacy && (
                  <div style={{ padding: 10, background: 'var(--color-warning-bg, #fff8e1)', borderRadius: 8, fontSize: 12, marginBottom: 10, lineHeight: 1.9 }}>
                    ⚠️ این پلن قدیمی با «نرخ مطلق» ساخته شده. با ذخیره، به مدل «تخفیف داینامیک»
                    تبدیل می‌شود (مشترک‌های فعال snapshot خود را نگه می‌دارند).
                  </div>
                )}
                <div className="edit-field full">
                  <label>درصد تخفیف روی کارمزد پایه (۱ تا ۹۰) *</label>
                  <input
                    type="number"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    placeholder="مثال: 30"
                    min={1}
                    max={90}
                    step={1}
                  />
                </div>
                {renderDiscountPreview()}
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDialogOpen(false)}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSave} disabled={saving}>
            {saving && <CircularProgress size={16} style={{ color: '#fff' }} />}
            ذخیره پلن
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default SellerPlansManagement;
